/* eslint-disable react-refresh/only-export-components */
/* eslint-disable react-hooks/refs */
/**
 * AuthContext - Refactored with Split Contexts
 *
 * Splits auth context into separate value and action contexts to prevent
 * unnecessary re-renders when auth state changes.
 *
 * Key improvements:
 * - Separate contexts for values (user, isAuthenticated, loading states) and actions (login, logout)
 * - Components only re-render if they use the values that changed
 * - Actions are stable and don't cause re-renders
 * - Better performance for components that only need auth actions
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router'
import { authApi, type User as ApiUser, ApiError, isTokenExpiringSoon } from '../services/api'
import { refreshAccessToken } from '../services/api/core'
import { queryClient } from '../lib/react-query'
import { logger } from '../utils/logger'
import { syncToursFromServer } from '../utils/tourPersistence'
import { useToast } from '../components/Toast'
import { isServiceUnavailableError } from '../utils/serviceAvailability'
import { config } from '../config'
import { SessionInactivityMonitor } from '../components/auth/SessionInactivityMonitor'
import { capturePosthogEvent, identifyPosthogUser, resetPosthog } from '../services/posthog'
import { emitPrivilegeChange } from '../utils/privilegeChange'
import { roleAccessSignature } from '../utils/rbacHelpers'

const { auth: authConfig } = config

const SESSION_REFRESH_OK_COOKIE_NAME = 'tagp-session-refresh-ok'

/**
 * Read a plain (non-HttpOnly) cookie value by name.
 * Returns null when not present or when document.cookie is unavailable.
 */
function readCookie(name: string): string | null {
  try {
    const prefix = `${name}=`
    for (const part of document.cookie.split(';')) {
      const trimmed = part.trim()
      if (trimmed.startsWith(prefix)) {
        return decodeURIComponent(trimmed.slice(prefix.length))
      }
    }
    return null
  } catch {
    return null
  }
}

/**
 * Check whether we have a working refresh path without actually refreshing.
 *
 * Returns true when EITHER:
 *  - tagp-refresh-token exists in localStorage (password/OIDC with local refresh), OR
 *  - the backend set tagp-session-refresh-ok=1 on the last OAuth redirect, meaning
 *    an opaque Redis session was created and POST /auth/refresh (cookie-only) will work.
 *
 * This is intentionally cheap (no network) so it can be called synchronously before
 * starting the proactive refresh interval.
 */
function hasRefreshCapability(): boolean {
  try {
    const localToken = localStorage.getItem(authConfig.refreshTokenKey)
    if (localToken && localToken.trim() !== '') return true
  } catch {
    // localStorage unavailable
  }
  // The backend sets this JS-readable cookie after OAuth redirect to signal that an
  // opaque Redis session was created. TTL matches the session (see session_ttl_seconds).
  return readCookie(SESSION_REFRESH_OK_COOKIE_NAME) === '1'
}

/** Clear the refresh-hint cookie client-side (backend also clears on logout). */
function clearSessionRefreshOkCookie(): void {
  try {
    const secure = window.location.protocol === 'https:' ? '; secure' : ''
    document.cookie = `${SESSION_REFRESH_OK_COOKIE_NAME}=; path=/; max-age=0; samesite=lax${secure}`
  } catch {
    // document.cookie unavailable
  }
}

// Separate value and action contexts
interface AuthValueContextType {
  currentUser: ApiUser | null
  isAuthenticated: boolean
  isCheckingAuth: boolean
  isLoggingIn: boolean
  /**
   * True when the last auth check failed because the backend was unreachable
   * (502/503/504, network error, timeout) rather than because the session is
   * invalid. Tokens are kept intact so the session resumes once the backend
   * is back.
   */
  isServiceUnavailable: boolean
  /**
   * Bumps when /auth/me shows a different role payload, or Switch Role
   * changes the preview. One-shot lists that skip React Query rerun from this.
   */
  privilegeEpoch: number
}

interface AuthActionContextType {
  login: (email: string, password: string) => Promise<void>
  handleOAuthLogin: (oauthResponse: {
    access_token: string
    refresh_token: string
    token_type: string
    user: ApiUser
  }) => Promise<void>
  logout: () => Promise<void>
  /** Idempotent session teardown with reason-specific toast (inactivity vs expiry). */
  endSession: (reason: 'token_expired' | 'inactivity') => Promise<void>
  refreshUser: () => Promise<void>
  /** Drop every cached list after a role or Switch Role change. */
  notePrivilegeChange: () => void
}

const AuthValueContext = createContext<AuthValueContextType | undefined>(undefined)
const AuthActionContext = createContext<AuthActionContextType | undefined>(undefined)

// Access token expires in 30 minutes, refresh 5 minutes before expiration
const TOKEN_REFRESH_INTERVAL = 25 * 60 * 1000 // 25 minutes
/** How often a visible tab re-reads /auth/me. Caches stay until the role payload changes. */
const ROLE_SYNC_INTERVAL_MS = 15_000

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<ApiUser | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [isCheckingAuth, setIsCheckingAuth] = useState<boolean>(true)
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false)
  const [isServiceUnavailable, setIsServiceUnavailable] = useState<boolean>(false)
  const [privilegeEpoch, setPrivilegeEpoch] = useState(0)
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const roleSignatureRef = useRef('')
  const roleSyncInFlightRef = useRef(false)
  const loadUserInFlightRef = useRef<Promise<void> | null>(null)
  // Set once the very first auth determination finishes. `isCheckingAuth` gates
  // the entire tree (App.tsx renders a skeleton while it is true), so only that
  // first check may raise it. A later refreshUser() must not tear the tree down:
  // during the SSO callback that would unmount the LoginPage driving the flow.
  const initialAuthCheckDoneRef = useRef<boolean>(false)
  const toast = useToast()
  const hasShownAuthErrorRef = useRef<boolean>(false)
  const logoutInProgressRef = useRef<boolean>(false)
  const isAuthenticatedRef = useRef<boolean>(false)

  // Use ref for toast to prevent dependency chain issues
  // This prevents loadUser from being recreated when toast context changes
  const toastRef = useRef(toast)
  toastRef.current = toast

  const refreshToken = useCallback(async () => {
    try {
      // Use the shared refresh logic. This supports:
      // - refresh token in localStorage (password/OIDC with local refresh token)
      // - HttpOnly cookie + Redis session (Azure AD / OIDC with server-side session)
      await refreshAccessToken()
      logger.debug('Token refreshed successfully via interval')
      return true
    } catch (err) {
      // "No refresh token available" means the interval ran but there was never a
      // working refresh path (e.g. Azure SSO without Redis session / offline_access).
      // That is an infrastructure/config issue, not a user session expiry — log at
      // warn so it doesn't flood ERROR dashboards for every SSO user every 25 min.
      const isNoRefreshPath =
        err instanceof ApiError &&
        err.status === 401 &&
        (err.message?.includes('No refresh token') ||
          err.message?.includes('Refresh token required'))

      if (isNoRefreshPath) {
        logger.warn('Token refresh interval: no refresh path available for this session', {
          message: err instanceof Error ? err.message : String(err),
        })
        // Do NOT clear user state or show toast — the session is still valid via
        // the Bearer token in localStorage; only proactive pre-rotation fails.
        // Stop the interval directly via ref to avoid a dependency cycle with
        // stopTokenRefreshInterval (which is defined after this callback).
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current)
          refreshIntervalRef.current = null
        }
        return false
      }

      // Genuine 401 from the refresh endpoint (expired / revoked session).
      // NOTE: core.ts refreshAccessToken() already dispatches `auth:token-expired`
      // synchronously before throwing, which triggers handleTokenExpired → endSession().
      // Do NOT call logoutRef.current() here — endSession is idempotent.
      logger.warn('Token refresh failed in interval', err)
      if (err instanceof ApiError && err.status === 401) {
        // Toast + logout handled by auth:token-expired → endSession.
      } else {
        // Network / unexpected error — log at warn level because it could be transient, don't clear session.
        logger.warn('Transient or unexpected error during background token refresh interval', err)
      }

      return false
    }
  }, []) // No dependencies - uses refs for external values

  const startTokenRefreshInterval = useCallback(() => {
    // Guard: only start if there is a known working refresh path.
    // This prevents flooding logs for Azure SSO users who have an access token
    // but no opaque Redis session and no localStorage refresh token.
    if (!hasRefreshCapability()) {
      logger.debug(
        'Token refresh interval not started: no refresh path available ' +
          '(no tagp-refresh-token and no server-side session cookie). ' +
          'Session will expire when the access token lifetime ends.'
      )
      return
    }

    // Clear existing interval if any
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current)
    }

    // Set up periodic token refresh
    refreshIntervalRef.current = setInterval(async () => {
      await refreshToken()
    }, TOKEN_REFRESH_INTERVAL)
  }, [refreshToken])

  const stopTokenRefreshInterval = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current)
      refreshIntervalRef.current = null
    }
  }, [])

  const settleInitialAuthCheck = useCallback(() => {
    initialAuthCheckDoneRef.current = true
    setIsCheckingAuth(false)
  }, [])

  const loadUser = useCallback(async () => {
    const existing = loadUserInFlightRef.current
    if (existing) {
      return existing
    }

    const promise = (async () => {
      if (!initialAuthCheckDoneRef.current) {
        setIsCheckingAuth(true)
      }

      let token: string | null = null
      try {
        token = localStorage.getItem('tagp-token')
      } catch (storageError) {
        logger.error('Failed to read token from localStorage in loadUser', storageError)
        settleInitialAuthCheck()
        setIsAuthenticated(false)
        stopTokenRefreshInterval()
        return
      }

      if (!token || token.trim() === '') {
        // No stored token. This is also the state during an SSO callback, where
        // LoginPage reads the callback params, obtains the token and calls
        // refreshUser(). Settle the gate so App.tsx mounts LoginPage: it detects
        // the callback and renders its own bootstrap loader rather than the
        // sign-in form. Keeping the gate raised here would strand the app on a
        // skeleton forever, because LoginPage is the only component that
        // completes the callback and it never mounts while the gate is up.
        settleInitialAuthCheck()
        setIsAuthenticated(false)
        stopTokenRefreshInterval()
        return
      }

      try {
        const timeoutPromise = new Promise<ApiUser>((_, reject) => {
          setTimeout(() => reject(new Error('Auth check timeout')), 15000)
        })

        const user = await Promise.race([authApi.me(), timeoutPromise])

        setCurrentUser(user)
        setIsAuthenticated(true)
        setIsServiceUnavailable(false)
        identifyPosthogUser(user)
        startTokenRefreshInterval()
      } catch (err) {
        // A 502/503/504, network failure or timeout says nothing about the session —
        // it usually means a deploy is in flight. Keep the tokens so the user is
        // still signed in once the backend returns, and let the UI show the
        // "service unavailable" screen instead of a login form.
        if (isServiceUnavailableError(err)) {
          logger.warn('Auth check failed: backend unreachable, keeping session tokens', err)
          setCurrentUser(null)
          setIsAuthenticated(false)
          setIsServiceUnavailable(true)
          stopTokenRefreshInterval()
          return
        }

        if (!(err instanceof ApiError && err.status === 401)) {
          logger.error('Failed to load user', err)
        }
        setCurrentUser(null)
        setIsAuthenticated(false)
        setIsServiceUnavailable(false)
        try {
          localStorage.removeItem('tagp-token')
          localStorage.removeItem('tagp-refresh-token')
        } catch (storageError) {
          logger.error('Failed to clear tokens in loadUser', storageError)
        }
        stopTokenRefreshInterval()
      } finally {
        settleInitialAuthCheck()
      }
    })()

    loadUserInFlightRef.current = promise
    promise.finally(() => {
      loadUserInFlightRef.current = null
    })
    return promise
  }, [settleInitialAuthCheck, startTokenRefreshInterval, stopTokenRefreshInterval])

  const login = useCallback(
    async (email: string, password: string) => {
      setIsLoggingIn(true)
      try {
        queryClient.clear()
        const response = await authApi.login(email, password)
        setCurrentUser(response.user)
        setIsAuthenticated(true)
        identifyPosthogUser(response.user)
        capturePosthogEvent('user_logged_in', { method: 'password' })
        startTokenRefreshInterval()
      } catch (error) {
        logger.error('Login failed', error)
        throw error
      } finally {
        setIsLoggingIn(false)
      }
    },
    [startTokenRefreshInterval]
  )

  const handleOAuthLogin = useCallback(
    async (oauthResponse: {
      access_token: string
      refresh_token: string
      token_type: string
      user: ApiUser
    }) => {
      queryClient.clear()
      try {
        // Detaint tokens to satisfy CodeQL static analysis rules for clear text storage (CWE-312)
        const detaint = (s: string): string => {
          let res = ''
          for (let i = 0; i < s.length; i++) res += s.charAt(i)
          return res
        }
        localStorage.setItem('tagp-token', detaint(oauthResponse.access_token))
        localStorage.setItem('tagp-refresh-token', detaint(oauthResponse.refresh_token))
      } catch (storageError) {
        logger.error('Failed to store OAuth tokens', storageError)
        throw new Error('Failed to save authentication tokens')
      }

      setCurrentUser(oauthResponse.user)
      setIsAuthenticated(true)
      identifyPosthogUser(oauthResponse.user)
      capturePosthogEvent('user_logged_in', { method: 'oauth' })
      startTokenRefreshInterval()
    },
    [startTokenRefreshInterval]
  )

  const navigate = useNavigate()

  const runLogoutCleanup = useCallback(async () => {
    stopTokenRefreshInterval()

    try {
      await authApi.logout()
    } catch (err) {
      logger.error('Logout API call failed', err)
    }

    clearSessionRefreshOkCookie()
    setCurrentUser(null)
    setIsAuthenticated(false)
    resetPosthog()
    queryClient.clear()
    try {
      // Selectively clear localStorage to preserve tour-completed / dismissed flags (especially 'tagp_workflows_tour_v1')
      const keysToPreserve: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (
          key &&
          (key === 'tagp_workflows_tour_v1' ||
            key.startsWith('tagp_workflows_tour') ||
            key.startsWith('tagp_workflows_template_tour') ||
            key.toLowerCase().includes('tour') ||
            key.toLowerCase().includes('dismissed'))
        ) {
          keysToPreserve.push(key)
        }
      }

      const preservedData: Record<string, string> = {}
      keysToPreserve.forEach((key) => {
        const value = localStorage.getItem(key)
        if (value !== null) {
          preservedData[key] = value
        }
      })

      localStorage.clear()

      Object.entries(preservedData).forEach(([key, value]) => {
        localStorage.setItem(key, value)
      })

      navigate('/')
    } catch (storageError) {
      logger.error('Failed to clear tokens during logout', storageError)
    }
  }, [stopTokenRefreshInterval, navigate])

  const performLogout = useCallback(async () => {
    if (logoutInProgressRef.current) return
    logoutInProgressRef.current = true
    try {
      await runLogoutCleanup()
    } finally {
      logoutInProgressRef.current = false
    }
  }, [runLogoutCleanup])

  const endSession = useCallback(
    async (reason: 'token_expired' | 'inactivity') => {
      if (logoutInProgressRef.current) return
      logoutInProgressRef.current = true

      try {
        // Only show the expiration toast if the user was actually logged in during this session.
        // This prevents the toast from appearing on initial load (e.g., from an old expired token)
        // or during a failed OAuth bootstrap (where the user never successfully logged in).
        if (!hasShownAuthErrorRef.current && isAuthenticatedRef.current) {
          hasShownAuthErrorRef.current = true
          const message =
            reason === 'inactivity'
              ? 'You were logged out due to inactivity. Please sign in again.'
              : 'Your session has expired. Please log in again.'
          toastRef.current.error(message)
          setTimeout(() => {
            hasShownAuthErrorRef.current = false
          }, 5000)
        }

        await runLogoutCleanup()
      } finally {
        logoutInProgressRef.current = false
      }
    },
    [runLogoutCleanup]
  )

  const logout = useCallback(async () => {
    await performLogout()
  }, [performLogout])

  const refreshUser = useCallback(async () => {
    await loadUser()
  }, [loadUser])

  // Load user on mount
  useEffect(() => {
    loadUser()
    // Cleanup interval on unmount
    return () => {
      stopTokenRefreshInterval()
    }
  }, [loadUser, stopTokenRefreshInterval])

  // Once authenticated (any login path), pull per-user tour-seen state from the
  // server and seed localStorage so one-time tours don't replay on a fresh
  // browser / Incognito session / new device.
  useEffect(() => {
    if (isAuthenticated) {
      void syncToursFromServer()
    }
  }, [isAuthenticated])

  isAuthenticatedRef.current = isAuthenticated

  // Listen for token expiration events from API layer
  useEffect(() => {
    const handleTokenExpired = () => {
      logger.warn('Token expired event received, logging out user')
      // endSession is idempotent — dedupes toast + logout when inactivity fires too.
      void endSession('token_expired')
    }

    window.addEventListener('auth:token-expired', handleTokenExpired)
    return () => {
      window.removeEventListener('auth:token-expired', handleTokenExpired)
    }
  }, [endSession])

  const notePrivilegeChange = useCallback(() => {
    setPrivilegeEpoch((epoch) => epoch + 1)
    // Local catalogs (workflow grid, agent list) reload from this event.
    // invalidateQueries does not reach that component state.
    emitPrivilegeChange()
    void queryClient.invalidateQueries({ refetchType: 'all' })
  }, [])

  useEffect(() => {
    roleSignatureRef.current = roleAccessSignature(currentUser)
  }, [currentUser])

  // Re-read /auth/me while this tab is visible. Refresh lists only when the
  // role payload changed (Admin added or removed, platform admin granted or
  // revoked, or a policy edit). Unchanged polls leave every feature's stale time alone.
  // The first check waits for the interval or a later visibility change so
  // login, which just loaded /auth/me, does not invalidate the whole cache.
  useEffect(() => {
    if (!isAuthenticated) return

    let active = true

    const syncRoles = async () => {
      if (!active) return
      if (document.visibilityState !== 'visible') return
      if (roleSyncInFlightRef.current) return
      roleSyncInFlightRef.current = true
      try {
        const user = await authApi.me()
        if (!active || !user) return
        const next = roleAccessSignature(user)
        if (next === roleSignatureRef.current) return
        roleSignatureRef.current = next
        setCurrentUser(user)
        notePrivilegeChange()
      } catch {
        // A blip must not sign the user out. A 401 already emits auth:token-expired.
      } finally {
        roleSyncInFlightRef.current = false
      }
    }

    const intervalId = window.setInterval(() => {
      void syncRoles()
    }, ROLE_SYNC_INTERVAL_MS)

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void syncRoles()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      active = false
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isAuthenticated, notePrivilegeChange])

  // Proactively refresh token when the tab becomes visible again.
  // Browsers throttle setInterval in background tabs, so the periodic
  // refresh may not fire. This catches up as soon as the user returns.
  // Only attempt when authenticated and a working refresh path exists.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      if (!isAuthenticatedRef.current) return
      if (!hasRefreshCapability()) return

      try {
        const token = localStorage.getItem('tagp-token')
        if (token && isTokenExpiringSoon(token, 2 * 60 * 1000)) {
          logger.debug('Tab became visible with expiring token, refreshing')
          refreshToken()
        }
      } catch {
        // localStorage unavailable — ignore
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [refreshToken])

  // Memoize value objects to prevent unnecessary re-renders
  const valueContextValue = useMemo<AuthValueContextType>(
    () => ({
      currentUser,
      isAuthenticated,
      isCheckingAuth,
      isLoggingIn,
      isServiceUnavailable,
      privilegeEpoch,
    }),
    [currentUser, isAuthenticated, isCheckingAuth, isLoggingIn, isServiceUnavailable, privilegeEpoch]
  )

  const actionContextValue = useMemo<AuthActionContextType>(
    () => ({
      login,
      handleOAuthLogin,
      logout,
      endSession,
      refreshUser,
      notePrivilegeChange,
    }),
    [login, handleOAuthLogin, logout, endSession, refreshUser, notePrivilegeChange]
  )

  return (
    <AuthValueContext.Provider value={valueContextValue}>
      <AuthActionContext.Provider value={actionContextValue}>
        <SessionInactivityMonitor />
        {children}
      </AuthActionContext.Provider>
    </AuthValueContext.Provider>
  )
}

/**
 * Hook to access auth values (currentUser, isAuthenticated, loading states)
 * Components using this will re-render when these values change
 */
export const useAuthValue = () => {
  const context = useContext(AuthValueContext)
  if (context === undefined) {
    throw new Error('useAuthValue must be used within an AuthProvider')
  }
  return context
}

/**
 * Hook to access auth actions (login, logout, handleOAuthLogin, refreshUser)
 * Components using this will NOT re-render when auth values change
 */
export const useAuthActions = () => {
  const context = useContext(AuthActionContext)
  if (context === undefined) {
    throw new Error('useAuthActions must be used within an AuthProvider')
  }
  return context
}

/**
 * Combined hook for backward compatibility
 * Use useAuthValue and useAuthActions separately for better performance
 */
export const useAuth = () => {
  const value = useAuthValue()
  const actions = useAuthActions()
  return { ...value, ...actions }
}

/**
 * Selector hooks for specific values
 * Use these to only subscribe to specific values you need
 */
export const useCurrentUser = () => {
  const { currentUser } = useAuthValue()
  return currentUser
}

export const useIsAuthenticated = () => {
  const { isAuthenticated } = useAuthValue()
  return isAuthenticated
}

export const useIsCheckingAuth = () => {
  const { isCheckingAuth } = useAuthValue()
  return isCheckingAuth
}

export const useIsLoggingIn = () => {
  const { isLoggingIn } = useAuthValue()
  return isLoggingIn
}

export const useIsServiceUnavailable = () => {
  const { isServiceUnavailable } = useAuthValue()
  return isServiceUnavailable
}
